import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Upload, X, FileText, CheckCircle2, Copy, MessageCircle, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { t, SERVICES, PRIMARY_WHATSAPP } from "../lib/i18n";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

const acceptFor = (a) => {
  if (a === "photo") return ".jpg,.jpeg,.png,image/jpeg,image/png";
  if (a === "pdf") return ".pdf,application/pdf";
  return ".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf";
};

const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
const ALLOWED_EXT = ["jpg", "jpeg", "png", "pdf"];
const MAX_SIZE = 15 * 1024 * 1024;

const SubmitRequestPage = () => {
  const { lang, tr } = useLang();
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", address: "",
    service_key: params.get("service") || SERVICES[0].key,
    custom_work: "", notes: "",
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [allDbServices, setAllDbServices] = useState([]);
  const [dbService, setDbService] = useState(null);
  const [dynFields, setDynFields] = useState({});
  const [dynFiles, setDynFiles] = useState({}); // { field_key: File }
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get("/services/public").then((r) => setAllDbServices(r.data || [])).catch(() => setAllDbServices([]));
  }, []);

  useEffect(() => {
    const s = params.get("service");
    if (s) {
      setForm((f) => ({ ...f, service_key: s }));
    }
  }, [params]);

  // Fetch dynamic service schema if available
  useEffect(() => {
    setDbService(null); setDynFields({});
    api.get(`/services/${form.service_key}`).then((r) => setDbService(r.data)).catch(() => setDbService(null));
  }, [form.service_key]);

  const activeService = useMemo(() => {
    if (dbService) return { key: dbService.key, en: { title: dbService.name_en }, hi: { title: dbService.name_hi } };
    return SERVICES.find((s) => s.key === form.service_key) || SERVICES[0];
  }, [form.service_key, dbService]);

  const visibleDynFields = useMemo(() => {
    if (!dbService?.form_schema) return [];
    return dbService.form_schema.filter((f) => {
      if (!f.condition_field) return true;
      return String(dynFields[f.condition_field] || "").toLowerCase() === String(f.condition_equals || "").toLowerCase();
    });
  }, [dbService, dynFields]);

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    const valid = [];
    for (const f of arr) {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED.includes(f.type) && !ALLOWED_EXT.includes(ext)) {
        toast.error(`${f.name}: ${tr({ en: "Unsupported type", hi: "अस्वीकृत प्रकार" })}`);
        continue;
      }
      if (f.size > MAX_SIZE) {
        toast.error(`${f.name}: ${tr({ en: "Too large (max 15MB)", hi: "बहुत बड़ी (अधिकतम 15MB)" })}`);
        continue;
      }
      valid.push(f);
    }
    setFiles((prev) => [...prev, ...valid]);
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const validate = () => {
    if (!form.name.trim())    return tr({ en: "Please enter your name", hi: "कृपया अपना नाम दर्ज करें" });
    if (!form.mobile.trim() || form.mobile.replace(/\D/g, "").length < 10)
      return tr({ en: "Please enter a valid 10-digit mobile", hi: "10 अंकों का सही मोबाइल दर्ज करें" });
    if (!form.address.trim()) return tr({ en: "Please enter your address", hi: "कृपया अपना पता दर्ज करें" });
    if (form.service_key === "other" && !form.custom_work.trim())
      return tr({ en: "Please describe your work", hi: "कृपया अपना काम बताएं" });
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("mobile", form.mobile.trim());
      if (form.email.trim()) fd.append("email", form.email.trim());
      fd.append("address", form.address.trim());
      fd.append("service_key", activeService.key);
      fd.append("service_name", activeService.en.title);
      if (form.custom_work.trim()) fd.append("custom_work", form.custom_work.trim());
      if (form.notes.trim()) fd.append("notes", form.notes.trim());
      if (Object.keys(dynFields).length > 0) fd.append("dynamic_fields", JSON.stringify(dynFields));
      files.forEach((f) => fd.append("files", f));
      // Dynamic file-type answers are uploaded as regular files too (labelled by key in filename prefix)
      Object.entries(dynFiles).forEach(([key, f]) => {
        if (f instanceof File) {
          const renamed = new File([f], `${key}__${f.name}`, { type: f.type });
          fd.append("files", renamed);
        }
      });

      const res = await api.post("/leads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      toast.success(tr({ en: "Request submitted successfully", hi: "आवेदन सफलतापूर्वक जमा हुआ" }));

      // Auto-redirect customer to WhatsApp with a pre-filled message TO Ranjit's number.
      // On mobile → opens the WhatsApp app. On desktop → opens web.whatsapp.com.
      // Because this is fired inside a user-gesture (form submit), most browsers allow it.
      const waMsg =
        `Namaste Ashish Point 🙏\n\n` +
        `Maine ek naya request submit kiya hai:\n` +
        `• Name: ${res.data.name}\n` +
        `• Mobile: ${res.data.mobile}\n` +
        `• Service: ${res.data.service_name}` +
        (res.data.custom_work ? `\n• Work: ${res.data.custom_work}` : "") +
        `\n• Request ID: ${res.data.request_id}\n\n` +
        `Kripya aage ki jaankari dijiye. Dhanyavaad!`;
      const waUrl = `https://wa.me/${PRIMARY_WHATSAPP}?text=${encodeURIComponent(waMsg)}`;
      // Small delay so success toast is visible
      setTimeout(() => {
        window.open(waUrl, "_blank", "noopener,noreferrer");
      }, 400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr({ en: "Submission failed", hi: "जमा नहीं हो सका" }));
    } finally {
      setSubmitting(false);
    }
  };

  const copyReqId = () => {
    if (result?.request_id) {
      navigator.clipboard.writeText(result.request_id);
      toast.success(tr({ en: "Request ID copied", hi: "अनुरोध आईडी कॉपी हो गयी" }));
    }
  };

  if (result) {
    const waMsg =
      `Namaste Ashish Point 🙏\n\n` +
      `Maine ek naya request submit kiya hai:\n` +
      `• Name: ${result.name}\n` +
      `• Mobile: ${result.mobile}\n` +
      `• Service: ${result.service_name}` +
      (result.custom_work ? `\n• Work: ${result.custom_work}` : "") +
      `\n• Request ID: ${result.request_id}\n\n` +
      `Kripya aage ki jaankari dijiye. Dhanyavaad!`;
    const waUrl = `https://wa.me/${PRIMARY_WHATSAPP}?text=${encodeURIComponent(waMsg)}`;
    return (
      <div data-testid="submit-success" className="max-w-3xl mx-auto px-4 lg:px-8 py-16">
        <div className="rounded-xl bg-white border border-slate-200 p-8 sm:p-10 shadow-sm">
          <div className="w-14 h-14 rounded-full bg-green-50 text-green-600 flex items-center justify-center mb-5">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900">
            {tr({ en: "Thank you — your request is received.", hi: "धन्यवाद — आपका आवेदन प्राप्त हुआ है।" })}
          </h1>
          <p className="mt-2 text-slate-600">{tr(t.common.saveCopy)}</p>

          <div className="mt-6 rounded-lg bg-teal-50 border border-teal-100 p-5 flex items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold">{tr(t.common.reqId)}</div>
              <div className="mt-1 font-heading text-2xl font-semibold text-slate-900" data-testid="success-request-id">{result.request_id}</div>
            </div>
            <Button variant="outline" onClick={copyReqId} data-testid="copy-request-id-btn">
              <Copy className="w-4 h-4 mr-2" /> {tr({ en: "Copy", hi: "कॉपी" })}
            </Button>
          </div>

          {/* Prominent WhatsApp send button — works on mobile & desktop */}
          <a
            href={waUrl}
            target="_blank" rel="noopener noreferrer"
            className="mt-4 flex items-center justify-center gap-2 h-12 rounded-md bg-green-600 hover:bg-green-700 text-white font-medium text-base transition-colors focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            data-testid="success-whatsapp-btn"
          >
            <MessageCircle className="w-5 h-5" />
            {tr({ en: "Send Details on WhatsApp", hi: "व्हाट्सएप पर विवरण भेजें" })}
          </a>
          <p className="mt-2 text-xs text-center text-slate-500">
            {tr({
              en: "If WhatsApp did not open automatically, tap the button above.",
              hi: "अगर WhatsApp अपने आप नहीं खुला, तो ऊपर वाला बटन दबाएं।",
            })}
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/track"><Button variant="outline" className="w-full" data-testid="go-to-track">{tr(t.nav.track)}</Button></Link>
            <Button
              variant="outline"
              onClick={() => { setResult(null); setFiles([]); setForm({ ...form, name: "", mobile: "", email: "", address: "", custom_work: "", notes: "" }); }}
              data-testid="submit-another"
            >
              {tr({ en: "Submit another", hi: "एक और आवेदन" })}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="submit-page" className="max-w-3xl mx-auto px-4 lg:px-8 py-12 lg:py-14">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.14em] text-teal-700 font-semibold">{tr(t.nav.submit)}</div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-heading font-semibold text-slate-900">
          {tr({ en: "Tell us what you need", hi: "बताइए आपको क्या चाहिए" })}
        </h1>
        <p className="mt-2 text-slate-600">{tr({ en: "Fill the form and attach documents. We'll message you on WhatsApp.", hi: "फॉर्म भरें और दस्तावेज़ जोड़ें। हम व्हाट्सएप पर संपर्क करेंगे।" })}</p>
      </div>

      <form onSubmit={submit} className="space-y-5 rounded-xl bg-white border border-slate-200 p-6 sm:p-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={tr(t.common.name)} required>
            <Input value={form.name} onChange={handleChange("name")} placeholder={tr({ en: "Your full name", hi: "आपका पूरा नाम" })} data-testid="lead-name-input" />
          </Field>
          <Field label={tr(t.common.mobile)} required>
            <Input inputMode="tel" value={form.mobile} onChange={handleChange("mobile")} placeholder="+91 70703 35050" data-testid="lead-mobile-input" />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={tr(t.common.email)}>
            <Input type="email" value={form.email} onChange={handleChange("email")} placeholder="you@gmail.com" data-testid="lead-email-input" />
          </Field>
          <Field label={tr(t.common.service)} required>
            <Select value={form.service_key} onValueChange={(v) => setForm({ ...form, service_key: v })}>
              <SelectTrigger data-testid="lead-service-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {(allDbServices.length > 0
                  ? [...allDbServices.map((s) => ({ key: s.key, en: { title: s.name_en }, hi: { title: s.name_hi } })), { key: "other", en: { title: "Other (Custom Request)" }, hi: { title: "अन्य (कस्टम अनुरोध)" } }]
                  : SERVICES
                ).map((s) => (
                  <SelectItem key={s.key} value={s.key} data-testid={`option-service-${s.key}`}>
                    {lang === "hi" ? s.hi.title : s.en.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        {/* Service photo header */}
        {dbService?.photo_file && (
          <div className="rounded-lg overflow-hidden border border-slate-200 h-40 bg-slate-100">
            <img src={`${API}/services/${dbService.key}/photo`} alt={dbService.name_en} className="w-full h-full object-cover" />
          </div>
        )}

        {/* Required documents checklist (from DB service) */}
        {dbService?.required_docs?.length > 0 && (
          <div className="rounded-md bg-teal-50 border border-teal-200 p-3">
            <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold mb-2">
              {tr({ en: "Required documents", hi: "आवश्यक दस्तावेज़" })}
              {dbService.fee && <span className="ml-2 text-slate-700 normal-case">· {tr({ en: "Fee", hi: "शुल्क" })}: {dbService.fee}</span>}
            </div>
            <ul className="text-sm text-slate-800 list-disc ml-5 space-y-0.5">
              {dbService.required_docs.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          </div>
        )}

        {/* Dynamic fields */}
        {visibleDynFields.length > 0 && (
          <div className="rounded-md border border-slate-200 p-3 space-y-3">
            <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold">
              {tr({ en: `${dbService.name_en} — extra details`, hi: `${dbService.name_hi} — अतिरिक्त विवरण` })}
            </div>
            {visibleDynFields.map((f) => (
              <div key={f.key}>
                <Label className="text-sm font-medium text-slate-800">{tr({ en: f.label_en, hi: f.label_hi || f.label_en })} {f.required && <span className="text-orange-600">*</span>}</Label>
                <div className="mt-1.5">
                  {f.type === "textarea" ? (
                    <Textarea rows={2} value={dynFields[f.key] || ""} onChange={(e) => setDynFields({ ...dynFields, [f.key]: e.target.value })} data-testid={`dyn-${f.key}`} />
                  ) : f.type === "dropdown" ? (
                    <Select value={dynFields[f.key] || ""} onValueChange={(v) => setDynFields({ ...dynFields, [f.key]: v })}>
                      <SelectTrigger data-testid={`dyn-${f.key}`}><SelectValue placeholder={tr({ en: "Select…", hi: "चुनें…" })} /></SelectTrigger>
                      <SelectContent>{(f.options || []).map((o) => <SelectItem key={o} value={o} data-testid={`dyn-opt-${f.key}-${o}`}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.type === "checkbox" ? (
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!dynFields[f.key]} onChange={(e) => setDynFields({ ...dynFields, [f.key]: e.target.checked })} data-testid={`dyn-${f.key}`} /> {tr({ en: "Yes", hi: "हाँ" })}</label>
                  ) : f.type === "file" ? (
                    <div>
                      <input type="file" accept={acceptFor(f.accept)} onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > MAX_SIZE) { toast.error(tr({ en: "File too large (max 15MB)", hi: "फ़ाइल बड़ी है (अधिकतम 15MB)" })); return; }
                        setDynFiles({ ...dynFiles, [f.key]: file });
                        setDynFields({ ...dynFields, [f.key]: file.name });
                      }} data-testid={`dyn-${f.key}`} className="text-sm" />
                      {dynFiles[f.key] && <div className="mt-1 text-xs text-slate-500 flex items-center gap-1"><FileText className="w-3 h-3" /> {dynFiles[f.key].name} ({(dynFiles[f.key].size / 1024).toFixed(1)} KB)</div>}
                      <div className="mt-1 text-xs text-slate-500">{f.accept === "photo" ? tr({ en: "Photo only (JPG/PNG)", hi: "केवल फोटो (JPG/PNG)" }) : f.accept === "pdf" ? tr({ en: "PDF only", hi: "केवल PDF" }) : tr({ en: "Photo or PDF", hi: "फोटो या PDF" })}</div>
                    </div>
                  ) : (
                    <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={dynFields[f.key] || ""} onChange={(e) => setDynFields({ ...dynFields, [f.key]: e.target.value })} data-testid={`dyn-${f.key}`} />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {form.service_key === "other" && (
          <Field label={tr(t.common.custom)} required>
            <Input value={form.custom_work} onChange={handleChange("custom_work")} placeholder={tr({ en: "e.g. Ration card correction", hi: "जैसे राशन कार्ड सुधार" })} data-testid="lead-custom-input" />
          </Field>
        )}

        <Field label={tr(t.common.address)} required>
          <Textarea rows={2} value={form.address} onChange={handleChange("address")} placeholder={tr({ en: "House, village, block, district, PIN", hi: "मकान, गाँव, प्रखंड, जिला, PIN" })} data-testid="lead-address-input" />
        </Field>

        <Field label={tr(t.common.notes)}>
          <Textarea rows={2} value={form.notes} onChange={handleChange("notes")} placeholder={tr({ en: "Anything else we should know?", hi: "और कुछ जो हमें बताना चाहें?" })} data-testid="lead-notes-input" />
        </Field>

        {/* File uploads */}
        <div>
          <Label className="text-sm font-medium text-slate-800">{tr(t.common.docs)}</Label>
          <div
            className="mt-2 rounded-md border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center cursor-pointer hover:border-teal-500 hover:bg-teal-50/40 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
            data-testid="dropzone"
          >
            <Upload className="w-6 h-6 mx-auto text-slate-500" />
            <div className="mt-2 text-sm font-medium text-slate-800">
              {tr({ en: "Tap or drop files to upload", hi: "फ़ाइल अपलोड करने हेतु टैप या ड्रॉप करें" })}
            </div>
            <div className="mt-1 text-xs text-slate-500">{tr(t.common.docsHint)}</div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="file-input"
            />
          </div>

          {files.length > 0 && (
            <ul className="mt-3 space-y-2" data-testid="file-list">
              {files.map((f, idx) => (
                <li key={idx} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-teal-700 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-slate-800 truncate">{f.name}</div>
                      <div className="text-xs text-slate-500">{(f.size / 1024).toFixed(1)} KB</div>
                    </div>
                  </div>
                  <button type="button" onClick={() => removeFile(idx)} className="text-slate-500 hover:text-red-600" data-testid={`remove-file-${idx}`} aria-label="Remove file">
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {files.length > 0 && (
            <button type="button" onClick={() => fileInputRef.current?.click()} className="mt-3 text-sm font-medium text-teal-700 hover:underline" data-testid="add-more-files-btn">
              + {tr(t.common.addMore)}
            </button>
          )}
        </div>

        <div className="pt-2">
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 bg-orange-600 hover:bg-orange-700 text-white text-base"
            data-testid="submit-lead-btn"
          >
            {submitting ? tr(t.common.submitting) : tr(t.common.submit)}
          </Button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <Label className="text-sm font-medium text-slate-800">
      {label} {required && <span className="text-orange-600">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export default SubmitRequestPage;
